import React, { useContext, useEffect, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'

import EventDetailsContext from '../../context/EventDetails/EventDetails'
import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import {
  getAccessControlSummary,
  getEventStatSheet,
  updateEventIsVisible,
} from '../../utilities/api'
import { checkPermission, getUrl } from '../../utilities/helpers'
import AuthService from '../../utilities/services/auth.service'
import useSafeAsync from '../../utilities/useSafeAsync'

import { getAccessControlSummaryTotals } from '../AccessControlWrapper/accessControlSummary'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { Reports } from '../Reports'
import { Cards } from './Cards'

const PUBLISHED_EVENT_STATUSES = ['published', 'on_sale', 'complete']

const isPublishedStatus = (event) =>
  PUBLISHED_EVENT_STATUSES.includes(event?.status)

export default function DashboardWrapper({ eventId }) {
  const safeSetState = useSafeAsync()

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const {
    event,
    isGAOnlyEvent,
    updateEvent,
    updateIsEventPublishing,
    hasEventEnded,
    updateIsEventOnsale,
    updateHasEventEnded,
    updateIsEventPublished,
    updateIsEventSoldout,
  } = useContext(EventDetailsContext)

  const { orgPermissions } = useContext(UserContext)

  const { getPermissions, getOrg } = AuthService

  // Same organization the Access Control Attendance Summary report runs as.
  const organization = getOrg()?.[0]

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT
    )

  const [show, setShow] = useState(false)

  const [stats, setStats] = useState()
  const [salesMetrics, setSalesMetrics] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [range, setRange] = useState({
    timePeriod: '24_hrs',
    type: 'primary',
  })

  const [sharableLink, setSharableLink] = useState()

  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }

    loadDashboardData()
  }, [range, orgPermissionsReady, hasPermission, eventId])

  /**
   * Totals come from the Access Control Attendance Summary only. There is no
   * fallback to the stat sheet, because those are different measures and
   * quietly swapping them makes the dashboard disagree with the report.
   */
  const loadSalesMetrics = async (eventStats) => {
    const organizationUUID =
      organization?.uuid ||
      eventStats?.organizationUUID ||
      event?.organizationUUID

    if (!organizationUUID) return null

    try {
      const response = await getAccessControlSummary(organizationUUID, eventId)
      return getAccessControlSummaryTotals(response.data, {
        uuid: organizationUUID,
        name: organization?.name,
      })
    } catch (err) {
      console.error(err)
      return null
    }
  }

  const loadDashboardData = async ({ refresh = false } = {}) => {
    if (refresh) {
      setIsRefreshing(true)
    } else {
      showLoading()
    }

    try {
      const statSheetResponse = await getEventStatSheet(eventId)
      const eventStats = statSheetResponse.data
      const nextSalesMetrics = await loadSalesMetrics(eventStats)

      safeSetState(() => {
        setStats(eventStats)
        setSalesMetrics(nextSalesMetrics)
        setIsVisible(Boolean(eventStats?.isVisible))
        // update context
        updateEvent(eventStats)
        updateIsEventOnsale(eventStats)
        updateHasEventEnded(eventStats)
        updateIsEventSoldout(eventStats)
        updateIsEventPublished(eventStats)
        updateIsEventPublishing(eventStats)
      })
    } catch (err) {
      console.error(err)
    } finally {
      safeSetState(() => {
        if (refresh) {
          setIsRefreshing(false)
        } else {
          hideLoading()
        }
      })
    }
  }

  useEffect(() => {
    if (!stats) return
    let defaultPromoCode = stats?.promoLinks.find(
      (stat) => stat.name === 'website'
    )?.code
    let shareLink
    if (isGAOnlyEvent) {
      shareLink = `${process.env.REACT_APP_CONSUMER_PORTAL}/e/${
        stats?.seoUrl
      }/${stats?.shortCode}?code=${defaultPromoCode ? defaultPromoCode : 0}`
    } else {
      shareLink = `${process.env.REACT_APP_CONSUMER_PORTAL}/e/${
        stats?.seoUrl
      }/${stats?.shortCode}/tickets?code=${
        defaultPromoCode ? defaultPromoCode : 0
      }`
    }
    setSharableLink(shareLink)
  }, [stats])

  const isPublished =
    isPublishedStatus(stats) ||
    (event?.uuid && event.uuid === stats?.uuid && isPublishedStatus(event))

  const handleVisibilityChange = (_, nextIsVisible) => {
    if (!isPublished) return
    setIsVisible(nextIsVisible)
    updateEvent({ ...stats, isVisible: nextIsVisible })
    updateEventIsVisible({
      data: {
        id: stats?.uuid,
        isVisible: nextIsVisible,
      },
    })
  }

  const handleShow = () => setShow(true)

  const handleClose = () => setShow(false)

  const onQRButtonClick = () => {
    const node = document.getElementById('qr')
    if (node == null) {
      return
    }

    // For canvas, we just extract the image data and send that directly.
    const dataURI = node.toDataURL('image/png')

    downloadStringAsFile(dataURI, `${stats?.seoUrl}.png`)
  }

  const downloadStringAsFile = (data, filename) => {
    let a = document.createElement('a')
    a.download = filename
    a.href = data
    a.click()
    handleClose()
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <div className="max-width-wrapper" data-testid="dashboard-container">
        <Reports stats={stats} setRange={setRange} />
        <Cards
          sharableLink={sharableLink}
          stats={stats}
          handleShow={handleShow}
          isVisible={isVisible}
          isPublished={isPublished}
          handleVisibilityChange={handleVisibilityChange}
          isDisabled={hasEventEnded}
          salesMetrics={salesMetrics}
          isRefreshing={isRefreshing}
          onRefresh={() => loadDashboardData({ refresh: true })}
        />
      </div>

      <Modal
        id="qr-modal"
        centered
        animation={false}
        fullscreen="md-down"
        show={show}
        onHide={handleClose}
        backdrop="static"
      >
        <Modal.Body>
          <div className="modal-body-heading">
            <h1 className="modal-body-heading-title">QR Code</h1>
          </div>
          <Stack direction="horizontal" className="justify-content-center">
            <QRCodeCanvas
              value={`${sharableLink}`}
              id="qr"
              size={260}
              includeMargin={true}
              marginSize={2}
              level={'L'}
            />
          </Stack>
          <p>
            This generated code is unique to your event. You can export and
            share it in PNG format.
          </p>
          <Stack
            direction="horizontal"
            className="btn-group-flex btn-group-flex-stretch"
          >
            <Button variant="outline-light" size="lg" onClick={handleClose}>
              Close
            </Button>
            <Button size="lg" onClick={onQRButtonClick}>
              Export
            </Button>
          </Stack>
        </Modal.Body>
      </Modal>
    </>
  )
}
